/**
 * Binder Requests — formally ask an underwriter to bind, and keep the binder.
 *
 * Josh, 2026-09-04: "Binder requests are formal requests to bind coverage."
 *
 * ⚠️ THE LIST IS ORDERED BY INCEPTION, NOT BY WHEN IT WAS SENT, AND THAT IS THE
 * WHOLE DIFFERENCE FROM LOSS RUNS. A loss run that has waited eleven days is the
 * urgent one. A binder request sent this morning for coverage incepting tomorrow
 * beats one sent three weeks ago for a January renewal. Sorting by age here
 * would rank a closing tomorrow below a policy nobody needs yet.
 *
 * ⚠️ AND "INCEPTS TODAY, NOT BOUND" IS THE ONE STATE THAT MUST SHOUT. Past that
 * line there is either a binder nobody recorded or a live coverage gap, and the
 * second one is an E&O claim. It gets red; everything else is calmer than it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { moduleById } from '@commpass/shared';
import { PageHero } from '../components/PageHero';
import { openedOnForm, prefill } from '../lib/newForm';
import { receiptState } from '../lib/receipt';
import { oneYearAfter } from '../lib/policyTerm';
import { StatCard } from '../components/StatCard';
import { useAuth } from '../auth/AuthProvider';
import { ApiError, api, type BinderRequest } from '../lib/api';

const STATUS_LABEL: Record<BinderRequest['status'], string> = {
  sent: 'Waiting',
  chased: 'Chased',
  bound: 'Bound',
  declined: 'Declined',
  closed: 'Closed',
};

const STATUS_TAG: Record<BinderRequest['status'], string> = {
  sent: 'is-draft',
  chased: 'is-questionnaire',
  bound: 'is-online',
  declined: 'is-withdrawn',
  closed: 'is-draft',
};

/** Whole days from today to an ISO date. Negative once it has passed. */
function daysUntil(iso: string): number {
  const then = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(then.getTime())) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((then.getTime() - today.getTime()) / 86400000);
}

const usDate = (iso: string | null): string => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  return y && m && d ? `${m}/${d}/${y}` : String(iso);
};

/** How the deadline reads, in the words somebody would say out loud. */
function whenText(days: number): string {
  if (days < 0) return `${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'} ago`;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

const initialsOf = (name: string): string =>
  String(name ?? '')
    .replace(/\b(inc|llc|ltd|co|corp|company|the|insurance|group)\b\.?/gi, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

const TABS = [
  { key: 'open', label: 'Awaiting a binder' },
  { key: 'bound', label: 'Bound' },
  { key: 'all', label: 'All' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/** The sentinel the limits dropdown uses for "let me type it". Not a real limit. */
const OTHER = '__other__';

const BLANK = {
  insuredName: '',
  mailingAddress: '',
  riskAddress: '',
  carrierName: '',
  quoteNumber: '',
  lineOfBusiness: '',
  limits: '',
  deductible: '',
  premium: '',
  effectiveDate: '',
  effectiveTime: '12:01 AM',
  expirationDate: '',
  lossPayee: '',
  notes: '',
};

/** What the sample letter listed as attachments. Named, not free text, so the
    letter says the same thing every time and the underwriter can scan it. */
const ATTACHMENTS = [
  'Signed application',
  'Loss runs',
  'Property photos / inspection report',
  'Premium payment confirmation',
  'Premium finance agreement',
];


/** Where a finished request lands. The list it joins, not the form it came from. */
const REQUESTS_HREF = moduleById('requests')?.href ?? '/hub/requests';

export function Binders(): JSX.Element {
  const navigate = useNavigate();
  const { email } = useAuth();

  const [rows, setRows] = useState<BinderRequest[] | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState<TabKey>('open');
  const [query, setQuery] = useState('');
  // Straight onto the form when the Requests Center sent you here to make one.
  const [showForm, setShowForm] = useState(openedOnForm);

  /*
    ⚠️ SEEDED FROM THE URL, THEN ORDINARY STATE. Connor hands these over when he
    starts a request for somebody (see lib/newForm.ts). A useState initialiser
    rather than an effect, so the field is filled on the first paint — an effect
    would show it empty and then fill it, which reads as the page correcting
    itself and makes people re-check what they just watched change.
  */
  const [form, setForm] = useState(() => ({
    ...BLANK,
    insuredName: prefill().insured ?? BLANK.insuredName,
    carrierName: prefill().carrier ?? BLANK.carrierName,
  }));
  const set = (k: keyof typeof BLANK, v: string) => setForm((f) => ({ ...f, [k]: v }));
  /** Whether an expiration date has been chosen deliberately. See LossRuns. */
  const [expTouched, setExpTouched] = useState(false);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [carriers, setCarriers] = useState<string[]>([]);
  /**
   * The lines being bound, and the limit for each.
   *
   * ⚠️ A MAP KEYED BY LINE, NOT TWO PARALLEL LISTS. Lines and limits held
   * separately drift the moment somebody deselects the middle one — and a limit
   * silently shifting onto the wrong line is the kind of error that gets bound.
   */
  const [lines, setLines] = useState<{ code: string; name: string; limits: string[] }[]>([]);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [suggested, setSuggested] = useState<{ name: string; email: string }[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [typedTo, setTypedTo] = useState('');
  const [replyTo, setReplyTo] = useState('');

  /** Marking one bound needs its number — see the route. */
  const [numberFor, setNumberFor] = useState('');
  const [binderNumber, setBinderNumber] = useState('');

  const load = () =>
    api
      .binderList()
      .then((r) => {
        setRows(r.requests);
        setErr('');
      })
      .catch((e: unknown) => {
        setRows([]);
        setErr(e instanceof ApiError ? e.message : 'Could not load your binder requests.');
      });

  useEffect(() => {
    void load();
    api.binderCarriers().then((r) => setCarriers(r.carriers)).catch(() => undefined);
    api.binderLines().then((r) => setLines(r.lines)).catch(() => undefined);
  }, []);

  const toggleLine = (name: string) =>
    setChosen((cur) => {
      if (name in cur) {
        const { [name]: _drop, ...rest } = cur;
        return rest;
      }
      return { ...cur, [name]: '' };
    });

  const [otherLine, setOtherLine] = useState('');
  /**
   * Which lines are having their limits typed rather than picked.
   *
   * ⚠️ ITS OWN STATE, NOT INFERRED FROM "the value is not in the list". A typed
   * limit that happens to match a preset would flip the field back to a dropdown
   * mid-edit, and the agent would watch what they were typing disappear.
   */
  const [custom, setCustom] = useState<Record<string, boolean>>({});

  /**
   * Add a line that is not one of the nineteen.
   *
   * ⚠️ A DUPLICATE IS A NO-OP, NOT A SECOND ROW. Typing "Commercial Auto" when
   * the chip is already on would otherwise produce two limits fields for one
   * line, and whichever was filled second would win silently.
   */
  const addOtherLine = () => {
    const name = otherLine.trim();
    if (!name) return;
    setChosen((cur) => (name in cur ? cur : { ...cur, [name]: '' }));
    setOtherLine('');
  };

  const coverages = useMemo(
    () => Object.entries(chosen).map(([line, limit]) => ({ line, limit })),
    [chosen],
  );

  useEffect(() => {
    if (email && !replyTo) setReplyTo(email);
  }, [email, replyTo]);

  useEffect(() => {
    const name = form.carrierName.trim();
    if (!name) {
      setSuggested([]);
      return;
    }
    api
      .binderContacts(name)
      .then((r) => setSuggested(r.contacts.map((c) => ({ name: c.name, email: c.email }))))
      .catch(() => setSuggested([]));
  }, [form.carrierName]);

  const all = useMemo(() => rows ?? [], [rows]);
  const loading = rows === null;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all
      .filter((r) => {
        const open = r.status === 'sent' || r.status === 'chased';
        if (tab === 'open') return open;
        if (tab === 'bound') return r.status === 'bound';
        return true;
      })
      .filter(
        (r) =>
          !q ||
          `${r.insured_name} ${r.carrier_name} ${r.id} ${r.quote_number} ${r.binder_number}`
            .toLowerCase()
            .includes(q),
      )
      // Soonest inception first — the only order that matches the urgency.
      .sort((a, b) => a.effective_date.localeCompare(b.effective_date));
  }, [all, tab, query]);

  const stats = useMemo(() => {
    const open = all.filter((r) => r.status === 'sent' || r.status === 'chased');
    const soon = open.filter((r) => daysUntil(r.effective_date) <= 3);
    const late = open.filter((r) => daysUntil(r.effective_date) < 0);
    return { open: open.length, soon: soon.length, late: late.length, bound: all.filter((r) => r.status === 'bound').length };
  }, [all]);

  const canSend =
    form.insuredName.trim() &&
    form.carrierName.trim() &&
    form.effectiveDate.trim() &&
    coverages.length > 0 &&
    (picked.length > 0 || typedTo.trim()) &&
    replyTo.trim();

  const recipients = useMemo(
    () => [...picked, ...typedTo.split(/[,;\s]+/).filter((s) => s.includes('@'))],
    [picked, typedTo],
  );

  const send = async () => {
    setBusy('send');
    setErr('');
    try {
      const out = await api.binderSend({ ...form, coverages, attachments, to: recipients, replyTo: replyTo.trim() });
      // Back to the dashboard the request now appears on. See lib/receipt.ts.
      navigate(
        REQUESTS_HREF,
        receiptState({
          id: out.id,
          headline: `Bind request sent. The underwriter's reply routes back to you automatically.`,
        }),
      );
      setShowForm(false);
      setForm({ ...BLANK });
      setExpTouched(false);
      setAttachments([]);
      setChosen({});
      setCustom({});
      setPicked([]);
      setTypedTo('');
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not send it.');
    } finally {
      setBusy('');
    }
  };

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id);
    setErr('');
    try {
      await fn();
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not update it.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="wrap">
      <PageHero
        title="Binder Requests"
        sub="Ask an underwriter to bind what they quoted, and keep the binder number that comes back."
        action={
          <button className="btn btn-primary" type="button" onClick={() => setShowForm((s) => !s)}>
            {showForm ? 'Back to the list' : '+ New request'}
          </button>
        }
      />

      {err && <div className="empty">{err}</div>}

      {!showForm && (
        <>
          <div className="stat-row">
            <StatCard
              tone="blue"
              icon="M9 12l2 2 4-4M7 3h10a2 2 0 012 2v14a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z"
              label="Awaiting a binder"
              value={stats.open}
              note="Asked, not yet bound"
              loading={loading}
            />
            <StatCard
              tone="amber"
              icon="M12 7v5l3 2M12 3a9 9 0 100 18 9 9 0 000-18z"
              label="Incepting within 3 days"
              value={stats.soon}
              note="Needs an answer now"
              loading={loading}
              slow={stats.soon > 0}
            />
            {/* ⚠️ ITS OWN CARD, IN RED. Past inception without a binder is either
                an unrecorded bind or a live coverage gap, and the second is an
                E&O claim. It does not belong folded into "waiting". */}
            <StatCard
              tone="amber"
              icon="M12 4l9 16H3zM12 10v4M12 17h.01"
              label="Past inception"
              value={stats.late}
              note={stats.late ? 'No binder — check for a gap' : 'Nothing overdue'}
              loading={loading}
              late={stats.late > 0}
            />
            <StatCard
              tone="green"
              icon="M12 3a9 9 0 100 18 9 9 0 000-18zM8 12l3 3 5-5"
              label="Bound"
              value={stats.bound}
              note="Binder number on file"
              loading={loading}
            />
          </div>

          <section className="panel lr-table-panel">
            <div className="lr-toolbar">
              <div className="lr-tabs" role="tablist">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.key}
                    className={tab === t.key ? 'is-on' : ''}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                    {t.key === 'open' && stats.open > 0 && <span className="lr-count">{stats.open}</span>}
                  </button>
                ))}
              </div>
              <div className="lr-search">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-4-4" />
                </svg>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search insured, carrier, quote…"
                  aria-label="Search binder requests"
                />
              </div>
            </div>

            {loading ? (
              <p className="lr-empty">Loading…</p>
            ) : !visible.length ? (
              <p className="lr-empty">
                {all.length ? 'Nothing in this tab.' : 'No binder requests yet.'}
                <span>
                  {all.length
                    ? 'Try another tab, or clear the search.'
                    : 'When a market has quoted and you are ready to bind, ask here and the chase looks after itself.'}
                </span>
              </p>
            ) : (
              <>
                <table className="lr-table">
                  <thead>
                    <tr>
                      <th>Insured</th>
                      <th>Carrier</th>
                      <th>Effective</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r) => {
                      const days = daysUntil(r.effective_date);
                      const open = r.status === 'sent' || r.status === 'chased';
                      return (
                        <tr key={r.id} className={open && days < 0 ? 'is-late-row' : ''}>
                          <td>
                            <div className="lr-cell">
                              <span className="lr-avatar" aria-hidden="true">
                                {initialsOf(r.insured_name)}
                              </span>
                              <div>
                                <b>{r.insured_name}</b>
                                <span>
                                  {r.id}
                                  {r.quote_number ? ` · quote ${r.quote_number}` : ''}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <b style={{ fontWeight: 600 }}>{r.carrier_name}</b>
                            <div className="lr-sub">{r.line_of_business || '—'}</div>
                          </td>
                          <td>
                            <b style={{ fontWeight: 600 }}>{usDate(r.effective_date)}</b>
                            <div className={`lr-sub${open && days <= 3 ? ' is-urgent' : ''}`}>
                              {open ? whenText(days) : `${r.effective_time}`}
                            </div>
                          </td>
                          <td>
                            <span className={`tag ${STATUS_TAG[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                            {r.binder_number && <div className="lr-sub">{r.binder_number}</div>}
                            {r.chase_count > 0 && open && <div className="lr-sub">chased {r.chase_count}×</div>}
                          </td>
                          <td className="sa-act">
                            {open &&
                              (numberFor === r.id ? (
                                <>
                                  <input
                                    className="lr-claiminput"
                                    autoFocus
                                    placeholder="Binder number"
                                    value={binderNumber}
                                    onChange={(e) => setBinderNumber(e.target.value)}
                                  />
                                  <button
                                    className="btn btn-sm btn-primary"
                                    type="button"
                                    disabled={!binderNumber.trim() || busy === r.id}
                                    onClick={() =>
                                      void act(r.id, async () => {
                                        await api.binderBound(r.id, binderNumber.trim());
                                        setNumberFor('');
                                        setBinderNumber('');
                                      })
                                    }
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="btn btn-sm btn-ghost"
                                    type="button"
                                    onClick={() => setNumberFor('')}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="btn btn-sm btn-primary"
                                    type="button"
                                    onClick={() => {
                                      setNumberFor(r.id);
                                      setBinderNumber('');
                                    }}
                                  >
                                    It&rsquo;s bound
                                  </button>
                                  <button
                                    className="btn btn-sm btn-ghost"
                                    type="button"
                                    disabled={busy === r.id}
                                    onClick={() => void act(r.id, () => api.binderClose(r.id, 'declined'))}
                                  >
                                    Declined
                                  </button>
                                </>
                              ))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="lr-count-line">
                  Showing {visible.length} of {all.length} request{all.length === 1 ? '' : 's'}
                </p>
              </>
            )}
          </section>

          <section className="panel lr-about">
            <div>
              <h3>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 3a9 9 0 100 18 9 9 0 000-18zM12 11v5M12 8h.01" />
                </svg>
                Asking to bind
              </h3>
              <p>
                A bind request is a formal instruction, not a question — the market has quoted and
                you are accepting. Say what incepts, when, and what is attached, and an underwriter
                can act on it without writing back.
              </p>
              <p className="lr-about-note">
                Follow-ups go out every two days and stop at the effective date. Past inception an
                email cannot help: either it bound and nobody recorded it, or there is a gap.
              </p>
            </div>
            <div>
              <h3>What to have ready</h3>
              <ul>
                <li>The quote number the underwriter will recognise</li>
                <li>Effective date and time — 12:01 AM unless somebody says otherwise</li>
                <li>Limits, deductible and premium as quoted</li>
                <li>Any lender or loss payee that must appear on the binder</li>
                <li>The signed application and anything else they asked for</li>
              </ul>
            </div>
          </section>
        </>
      )}

      {showForm && (
        <div className="pu-col">
          <section className="pu-panel">
            <div className="pu-head">
              <div>
                <h3>The risk</h3>
                <p>What is being bound, and for whom.</p>
              </div>
            </div>
            <div className="sf-fields">
              <div className="sf-field">
                <label htmlFor="b-insured">
                  Named insured<span className="pu-req"> *</span>
                </label>
                <input id="b-insured" value={form.insuredName} onChange={(e) => set('insuredName', e.target.value)} />
              </div>
              <div className="sf-field">
                <label htmlFor="b-carrier">
                  Carrier<span className="pu-req"> *</span>
                </label>
                <input
                  id="b-carrier"
                  list="b-carriers"
                  value={form.carrierName}
                  onChange={(e) => set('carrierName', e.target.value)}
                />
                <datalist id="b-carriers">
                  {carriers.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
              <div className="sf-field is-wide">
                <label htmlFor="b-mail">Mailing address</label>
                <input id="b-mail" value={form.mailingAddress} onChange={(e) => set('mailingAddress', e.target.value)} />
              </div>
              <div className="sf-field is-wide">
                <label htmlFor="b-risk">Location of risk (if different)</label>
                <input id="b-risk" value={form.riskAddress} onChange={(e) => set('riskAddress', e.target.value)} />
              </div>
              <div className="sf-field">
                <label htmlFor="b-quote">Quote number</label>
                <input id="b-quote" value={form.quoteNumber} onChange={(e) => set('quoteNumber', e.target.value)} />
              </div>
            </div>

            {/* ⚠️ MULTI-SELECT, BECAUSE ONE BINDER ROUTINELY COVERS SEVERAL
                LINES. A single text box forced somebody to type "GL, Auto, WC"
                and then squeeze three different limits into one more box. */}
            <div className="sf-field is-wide" style={{ marginTop: 4 }}>
              <label>
                Lines being bound<span className="pu-req"> *</span>
              </label>
              <div className="ins-chips">
                {lines.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    className={`ins-chip${l.name in chosen ? ' is-on' : ''}`}
                    onClick={() => toggleLine(l.name)}
                  >
                    {l.name}
                  </button>
                ))}
                {/* Lines typed in rather than picked. They look identical once
                    added, because to the underwriter they are identical. */}
                {Object.keys(chosen)
                  .filter((name) => !lines.some((l) => l.name === name))
                  .map((name) => (
                    <button
                      key={name}
                      type="button"
                      className="ins-chip is-on"
                      title="Remove"
                      onClick={() => toggleLine(name)}
                    >
                      {name} <span aria-hidden="true">×</span>
                    </button>
                  ))}
              </div>

              {/*
                ⚠️ "OTHER" IS A TEXT BOX, NOT A CHIP CALLED OTHER. A line
                literally named "Other" would print as "Other: 1M / 2M" in the
                letter, which tells the underwriter nothing and looks like a
                mistake on a document that binds coverage. The agent names the
                line and the letter says the name.

                The nineteen presets are the common ones, not the only ones —
                excess flood, kidnap and ransom, a manuscript form. This is how
                those get in.
              */}
              <div className="ins-other">
                <input
                  type="text"
                  value={otherLine}
                  placeholder="Another line — type it and press Add"
                  aria-label="Add a line not listed"
                  onChange={(e) => setOtherLine(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addOtherLine();
                    }
                  }}
                />
                <button
                  className="btn btn-sm btn-ghost"
                  type="button"
                  disabled={!otherLine.trim()}
                  onClick={addOtherLine}
                >
                  Add
                </button>
              </div>
            </div>
          </section>

          <section className="pu-panel">
            <div className="pu-head">
              <div>
                <h3>Terms and dates</h3>
                <p>
                  The effective date is the deadline everything here works to — and the time matters
                  on a closing.
                </p>
              </div>
            </div>
            <div className="sf-fields">
              <div className="sf-field">
                <label htmlFor="b-eff">
                  Effective date<span className="pu-req"> *</span>
                </label>
                <input
                  id="b-eff"
                  type="date"
                  value={form.effectiveDate}
                  onChange={(e) => {
                    set('effectiveDate', e.target.value);
                    // Same rule as the loss run form: annual unless told otherwise.
                    if (!expTouched) set('expirationDate', oneYearAfter(e.target.value));
                  }}
                />
              </div>
              <div className="sf-field">
                <label htmlFor="b-time">Effective time</label>
                <input id="b-time" value={form.effectiveTime} onChange={(e) => set('effectiveTime', e.target.value)} />
              </div>
              <div className="sf-field">
                <label htmlFor="b-exp">Expiration date</label>
                <input
                  id="b-exp"
                  type="date"
                  value={form.expirationDate}
                  onChange={(e) => {
                    set('expirationDate', e.target.value);
                    setExpTouched(true);
                  }}
                />
              </div>
              <div className="sf-field">
                <label htmlFor="b-ded">Deductible</label>
                <input id="b-ded" value={form.deductible} onChange={(e) => set('deductible', e.target.value)} />
              </div>
              <div className="sf-field">
                <label htmlFor="b-prem">Premium</label>
                <input id="b-prem" value={form.premium} onChange={(e) => set('premium', e.target.value)} />
              </div>
              <div className="sf-field is-wide">
                <label htmlFor="b-payee">Loss payee / mortgagee</label>
                <input id="b-payee" value={form.lossPayee} onChange={(e) => set('lossPayee', e.target.value)} />
              </div>
              <div className="sf-field is-wide">
                <label htmlFor="b-notes">Anything else the underwriter needs</label>
                <textarea id="b-notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
              </div>
            </div>

            {/*
              ⚠️ A LIMIT PER LINE, WITH THAT LINE'S OWN SUGGESTIONS. "1M / 2M"
              means something on general liability and nothing on workers
              compensation, where the answer is statutory. One box for all of
              them forced somebody to invent a format.

              ⚠️ AND IT IS A DATALIST, NOT A SELECT. These are the common
              answers, not the only legal ones — a layered tower or a scheduled
              value is on no list, and a dropdown that refused them would push
              the real limit into the notes, where an underwriter reads it last.
            */}
            {coverages.length > 0 && (
              <div className="sf-fields" style={{ paddingTop: 4 }}>
                {coverages.map(({ line, limit }) => {
                  const preset = lines.find((l) => l.name === line);
                  const options = preset?.limits ?? [];
                  const typing = custom[line] || options.length === 0;
                  return (
                    <div className="sf-field" key={line}>
                      <label htmlFor={`lim-${line}`}>{line} limits</label>
                      {typing ? (
                        <>
                          <input
                            id={`lim-${line}`}
                            type="text"
                            autoFocus={custom[line]}
                            value={limit}
                            placeholder="Type the limits as quoted"
                            onChange={(e) => setChosen((cur) => ({ ...cur, [line]: e.target.value }))}
                          />
                          {/* Only offered when there was a list to come back to —
                              a typed-in line has none. */}
                          {options.length > 0 && (
                            <button
                              type="button"
                              className="sf-inline-link"
                              onClick={() => {
                                setCustom((c) => ({ ...c, [line]: false }));
                                setChosen((cur) => ({ ...cur, [line]: '' }));
                              }}
                            >
                              Back to the list
                            </button>
                          )}
                        </>
                      ) : (
                        <select
                          id={`lim-${line}`}
                          value={limit}
                          onChange={(e) => {
                            if (e.target.value === OTHER) {
                              setCustom((c) => ({ ...c, [line]: true }));
                              setChosen((cur) => ({ ...cur, [line]: '' }));
                            } else {
                              setChosen((cur) => ({ ...cur, [line]: e.target.value }));
                            }
                          }}
                        >
                          <option value="">Select {line.toLowerCase()} limits…</option>
                          {options.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                          {/*
                            ⚠️ THE ESCAPE HATCH IS INSIDE THE DROPDOWN, NOT BESIDE
                            IT. Somebody whose limits are not on the list is
                            already looking at the list; a separate control
                            elsewhere on the panel is one they have to go and
                            find, and the usual outcome is picking the nearest
                            wrong option instead.
                          */}
                          <option value={OTHER}>Other — type the limits…</option>
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="pu-panel">
            <div className="pu-head">
              <div>
                <h3>Attached and sent to</h3>
                <p>
                  Tick what is going with it — the letter lists them so the underwriter can check
                  nothing is missing before they issue.
                </p>
              </div>
            </div>

            <div className="ins-chips" style={{ marginTop: 14 }}>
              {ATTACHMENTS.map((a) => (
                <button
                  key={a}
                  type="button"
                  className={`ins-chip${attachments.includes(a) ? ' is-on' : ''}`}
                  onClick={() =>
                    setAttachments((cur) => (cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a]))
                  }
                >
                  {a}
                </button>
              ))}
            </div>

            {suggested.length > 0 && (
              <div className="lr-contacts" style={{ marginTop: 16 }}>
                {suggested.map((c) => (
                  <label key={c.email} className="lr-contact">
                    <input
                      type="checkbox"
                      checked={picked.includes(c.email)}
                      onChange={() =>
                        setPicked((cur) =>
                          cur.includes(c.email) ? cur.filter((x) => x !== c.email) : [...cur, c.email],
                        )
                      }
                    />
                    <div>
                      <b>{c.name}</b>
                      <div className="sf-help">{c.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div className="sf-fields">
              <div className="sf-field is-wide">
                <label htmlFor="b-to">
                  {/* ⚠️ FIRST-CLASS, NOT A FALLBACK. A bind request goes to the
                      underwriter holding the quote, who is frequently not in our
                      contact book at all. */}
                  Underwriter&rsquo;s email<span className="pu-req"> *</span>
                </label>
                <input
                  id="b-to"
                  value={typedTo}
                  onChange={(e) => setTypedTo(e.target.value)}
                  placeholder={suggested.length ? 'Or add another address' : 'underwriter@carrier.com'}
                />
              </div>
              <div className="sf-field is-wide">
                <label htmlFor="b-reply">
                  Replies come back to<span className="pu-req"> *</span>
                </label>
                <input id="b-reply" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />
              </div>
            </div>

            <div className="supp-actions">
              <div className="supp-buttons">
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={!canSend || busy === 'send'}
                  onClick={() => void send()}
                >
                  {busy === 'send' ? 'Sending…' : `Send to ${recipients.length || 0}`}
                </button>
                <button className="btn btn-ghost" type="button" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
              </div>
              <p className="supp-note">
                {canSend
                  ? 'A reference is added at the foot so the reply finds its way back to you.'
                  : 'Insured, carrier, at least one line, effective date and a recipient are needed.'}
              </p>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
